// Per-state T-permit content for /t-permit/by-state/{slug}.
//
// These are spokes off the /t-permit hub, and they exist because permit rules
// are administered by the state RTO, not centrally — the cost, the paperwork and
// the end-of-life ceiling all differ across a border a Delhi NCR operator crosses
// most days. The hub answers "what is a T-permit"; these answer "what does it
// mean where I actually operate".
//
// Three states only, and deliberately so: Delhi, Haryana and Uttar Pradesh are
// the NCR triangle where BST holds essentially all of its stock (Gurugram 27,
// New Delhi 24). Writing a Maharashtra or Karnataka page would be the same
// mistake as a city page with no cars — authority we cannot back with inventory.
//
// Figures here are the ones already published across the linked guides. Where a
// number moves with RTO circulars the copy says so rather than pretending to a
// precision it does not have.

export const STATES = {
  delhi: {
    slug: 'delhi',
    // AITP fee (the "Crossing into NCR" fact) corrected to the 2023 rules on
    // 2026-09-29. Drives the visible "Updated" line and dateModified.
    updatedAt: '2026-09-29',
    name: 'Delhi',
    plate: 'DL-1R / yellow plate',
    title: 'T-Permit Rules in Delhi — Cost & Process',
    description:
      'How a T-permit works in Delhi: contract carriage vs AITP, the CNG-only reality, NGT age limits, what the RTO asks for, and where a Delhi permit stops.',
    keywords:
      'T permit Delhi, Delhi taxi permit, contract carriage permit Delhi, yellow plate Delhi, DL1R permit, Delhi commercial vehicle permit, NGT age limit Delhi taxi, AITP Delhi',
    standfirst:
      'Delhi is the strictest permit regime in the NCR triangle, and the one most likely to catch out a buyer who shopped on price alone.',
    intro: [
      'Delhi issues commercial permits through the Transport Department at the zonal RTOs, and a city taxi here runs on a contract carriage permit with a DL-1R series yellow plate. The permit itself is not the expensive part — what shapes the economics is everything attached to it: the fuel the vehicle is allowed to burn, how many years it has left, and how far it can go before it needs a second permit.',
      'Two rules dominate. New city taxi permits are effectively CNG-only, so a diesel car bought cheap outside Delhi may simply not be permittable as a Delhi city taxi. And the NGT end-of-life limits — 10 years for diesel, 15 for petrol — apply across the NCR districts, not just inside the Delhi boundary. Those limits cap your earning years and, because every subsequent buyer faces the same ceiling, most of the resale value too.',
      'The third thing to understand is the border. A Delhi contract carriage permit covers work inside Delhi. Airport runs to Gurugram, a corporate pickup in Noida, a drop to Faridabad — those cross state lines, and doing them regularly on a Delhi-only permit invites trouble. Most serious Delhi operators carry an All India Tourist Permit alongside, which removes the border question entirely.',
    ],
    facts: [
      ['Permit type for city work', 'Contract carriage, issued by the Delhi Transport Department'],
      ['Plate series', 'DL-1R, yellow plate with black lettering'],
      ['Fuel reality', 'New city taxi permits are effectively CNG-only'],
      ['Age ceiling', '10 years diesel, 15 years petrol — NGT, across NCR'],
      ['Crossing into NCR', 'Needs an AITP in practice — ₹20,000–₹30,000 a year for cars and MPVs (2023 rules)'],
      ['Driver requirement', 'Commercial licence plus a PSV badge'],
    ],
    faqIntent: [
      'Whether a diesel car can be run as a Delhi taxi',
      'How many years are left on a given vehicle',
      'Whether a Delhi permit covers Gurugram and Noida runs',
    ],
    guides: [
      ['delhi-taxi-permit-2026', 'Delhi Taxi Permit Rules: Tourist, Contract & Radio Cab'],
      ['yellow-plate-taxi-registration-delhi', 'Registering a Yellow Plate Taxi in Delhi'],
      ['aitp-all-india-tourist-permit-delhi', 'All India Tourist Permit: Cost, Documents & Process'],
      ['psv-badge-commercial-licence-india', 'PSV Badge & Commercial Driving Licence'],
      ['t-permit-cost-renewal-validity-india', 'T-Permit Cost, Validity & Renewal'],
    ],
    cityLink: ['/buy-taxi-delhi', 'used taxis for sale in Delhi'],
  },

  haryana: {
    slug: 'haryana',
    name: 'Haryana',
    plate: 'HR series / yellow plate',
    title: 'T-Permit Rules in Haryana — Gurugram Guide',
    description:
      'Haryana T-permit rules for Gurugram and Faridabad: HR contract carriage permits, why AITP is near-standard, and the NCR age limits that catch buyers out.',
    keywords:
      'T permit Haryana, Gurugram taxi permit, HR permit taxi, Haryana contract carriage permit, yellow plate Gurugram, Faridabad taxi permit, employee transport permit Haryana',
    standfirst:
      'Haryana is where most of BST’s stock actually sits, and where the permit question is dominated by one fact: almost every vehicle crosses into Delhi daily.',
    intro: [
      'A Haryana commercial vehicle runs on an HR-series yellow plate with a state contract carriage permit, issued through the district RTO. For a Gurugram operator that permit covers work inside Haryana — Cyber City, Udyog Vihar, Golf Course Road, Sohna Road, Manesar — which is a large and profitable market on its own.',
      'It is also, in practice, not enough. IGI airport is 15–20 km from most of Gurugram and sits in Delhi. Employee transport contracts routinely pick up across the border. A vehicle doing airport and corporate work on a Haryana-only permit is crossing a state line several times a day, which is why an All India Tourist Permit is close to standard here rather than an upgrade. Cost it in from the start, not as an afterthought.',
      'The age rules are the same trap as in Delhi and are frequently misunderstood: the NCR end-of-life limits — 10 years diesel, 15 petrol — apply to the NCR districts including Gurugram and Faridabad, not merely inside Delhi. A Haryana registration does not buy extra years. Remaining permissible age is the first thing to check on any Gurugram vehicle, before condition and before price.',
    ],
    facts: [
      ['Permit type for state work', 'Haryana contract carriage, issued by the district RTO'],
      ['Plate series', 'HR series, yellow plate'],
      ['Crossing into Delhi', 'AITP is near-standard, not optional, for airport and corporate duty'],
      ['Age ceiling', '10 years diesel, 15 years petrol — applies across NCR districts'],
      ['Dominant work', 'Corporate and employee transport, plus IGI airport duty'],
      ['Driver requirement', 'Commercial licence plus a PSV badge'],
    ],
    faqIntent: [
      'Whether a Haryana permit alone covers airport runs',
      'Whether NCR age limits apply outside Delhi',
      'What employee-transport contracts require of a vehicle',
    ],
    guides: [
      ['t-permit-cost-renewal-validity-india', 'T-Permit Cost, Validity & Renewal'],
      ['aitp-all-india-tourist-permit-delhi', 'All India Tourist Permit: Cost, Documents & Process'],
      ['commercial-car-registration-process-india', 'Commercial Registration & Yellow Plate Process'],
      ['t-permit-car-documents-checklist-india', 'T-Permit Documents Checklist Before You Buy'],
      ['psv-badge-commercial-licence-india', 'PSV Badge & Commercial Driving Licence'],
    ],
    cityLink: ['/buy-taxi-gurugram', 'used taxis for sale in Gurugram'],
  },

  'uttar-pradesh': {
    slug: 'uttar-pradesh',
    name: 'Uttar Pradesh',
    plate: 'UP-16 / UP-14 / yellow plate',
    title: 'T-Permit Rules in Uttar Pradesh — UP Guide',
    description:
      'UP T-permit rules for Noida and Ghaziabad: UP-16 and UP-14 contract carriage permits, the Delhi border problem, and which districts NCR age limits cover.',
    keywords:
      'T permit Uttar Pradesh, Noida taxi permit, UP16 permit, Ghaziabad taxi permit, UP contract carriage permit, yellow plate Noida, UP commercial vehicle permit, Ayodhya taxi route',
    standfirst:
      'UP is the largest of the three by geography and the most varied — an NCR commuter market at one end and a pilgrimage-circuit market at the other.',
    intro: [
      'Uttar Pradesh issues commercial permits through the regional transport offices, and NCR operators will meet two series most often: UP-16 for Gautam Buddh Nagar (Noida and Greater Noida) and UP-14 for Ghaziabad. Both carry a state contract carriage permit on a yellow plate.',
      'For a Noida or Ghaziabad vehicle the practical problem is identical to Haryana’s: the work crosses into Delhi most days. The Sector 62 and 63 IT belt, the Expressway campuses and the DND and Chilla commuter flows all generate duty that does not respect the state line, so an AITP is the usual addition rather than an exception. Running commercial work in UP on a Delhi permit — or the reverse — is where operators get caught.',
      'Away from NCR the market changes character entirely. The Ayodhya circuit, together with established Kanpur, Prayagraj and Varanasi runs, is outstation and multi-day work where an AITP is not a border convenience but the basis of the business. The same permit vocabulary, a completely different vehicle choice: comfortable sedans and MPVs rather than CNG city hatchbacks. Note that the NCR age ceilings bind in Gautam Buddh Nagar and Ghaziabad but are not a statewide UP rule — check the district, not just the state.',
    ],
    facts: [
      ['Permit type for state work', 'UP contract carriage, issued by the regional RTO'],
      ['Plate series', 'UP-16 (Noida / Greater Noida), UP-14 (Ghaziabad)'],
      ['Crossing into Delhi', 'AITP is the usual addition for NCR-based vehicles'],
      ['Age ceiling', 'NCR limits bind in Gautam Buddh Nagar and Ghaziabad — check the district'],
      ['Outstation work', 'Ayodhya, Kanpur, Prayagraj and Varanasi circuits run on AITP'],
      ['Driver requirement', 'Commercial licence plus a PSV badge'],
    ],
    faqIntent: [
      'Whether a Delhi-registered car can do commercial work in Noida',
      'Which districts the NCR age limits actually cover',
      'What the pilgrimage circuits require of a vehicle',
    ],
    guides: [
      ['aitp-all-india-tourist-permit-delhi', 'All India Tourist Permit: Cost, Documents & Process'],
      ['t-permit-cost-renewal-validity-india', 'T-Permit Cost, Validity & Renewal'],
      ['commercial-vehicle-permit-types-india', 'Every Commercial Vehicle Permit Type, Explained'],
      ['t-permit-car-documents-checklist-india', 'T-Permit Documents Checklist Before You Buy'],
      ['convert-private-car-to-t-permit-commercial', 'Converting a Private Car to a T-Permit Commercial Car'],
    ],
    cityLink: ['/buy-taxi-noida', 'used taxis for Noida buyers'],
  },
};

export const STATE_SLUGS = Object.keys(STATES);
